/**
 * WebGPU capability probe. Pure, no imports, safe to call anywhere.
 *
 * The gate is honest rather than apologetic: a visitor without WebGPU gets an
 * explanation of what the page would have done and which browsers do it, not
 * a shrug.
 */

export type GpuReport = {
  supported: boolean;
  reason?: string;
  /** shader-f16 lets us use the q4f16 weights, which are ~10% smaller and faster. */
  f16: boolean;
  vendor?: string;
  architecture?: string;
  /** Single-buffer cap. Weights above this must be chunked by the runtime. */
  maxBufferBytes?: number;
  /** Origin storage quota. Model weights are cached here between visits. */
  quotaBytes?: number;
  usageBytes?: number;
};

export async function probeGpu(): Promise<GpuReport> {
  const out: GpuReport = { supported: false, f16: false };

  try {
    const est = await navigator.storage?.estimate?.();
    if (est) {
      out.quotaBytes = est.quota ?? undefined;
      out.usageBytes = est.usage ?? undefined;
    }
  } catch {
    /* storage estimate is advisory only */
  }

  if (!("gpu" in navigator) || !navigator.gpu) {
    out.reason = "This browser does not expose WebGPU.";
    return out;
  }

  try {
    const adapter = await navigator.gpu.requestAdapter();
    if (!adapter) {
      out.reason = "WebGPU is present but no adapter was available. On laptops this usually means the GPU is disabled for this process.";
      return out;
    }

    out.f16 = adapter.features.has("shader-f16");
    out.maxBufferBytes = adapter.limits.maxBufferSize;

    const info = adapter.info as GPUAdapterInfo | undefined;
    if (info) {
      out.vendor = info.vendor || undefined;
      out.architecture = info.architecture || undefined;
    }

    // Requesting the device is the only test that proves the whole path works.
    // An adapter can exist and still fail here.
    const device = await adapter.requestDevice();
    if (!device) {
      out.reason = "A WebGPU adapter was found but the device could not be created.";
      return out;
    }
    device.destroy();

    out.supported = true;
    return out;
  } catch (err) {
    out.reason = `WebGPU failed to start: ${String(err)}`;
    return out;
  }
}

export function formatBytes(n: number | undefined): string {
  if (n === undefined || !Number.isFinite(n)) return "unknown";
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(0)} MB`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)} kB`;
  return `${n} B`;
}
